import { useState, useCallback, useRef } from "react";
import {
  ValidationErrors,
  useLessonPlannerValidationResponse,
} from "./useLessonPlannerValidation.interface";

const MIN_LEARNING_OBJECTIVE_LENGTH = 10;
export const MAX_LEARNING_OBJECTIVE_LENGTH = 500;

const useLessonPlannerValidation = (): useLessonPlannerValidationResponse => {
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    learningObjective: "",
    standards: "",
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateFields = useCallback(
    (learningObjective: string, standards: any[]): boolean => {
      const errors: ValidationErrors = {
        learningObjective: (() => {
          const len = (learningObjective ?? "").trim().length;
          switch (true) {
            case !learningObjective:
              return "You must include a learning objective";
            case len < MIN_LEARNING_OBJECTIVE_LENGTH:
              return `Learning objective is too short (minimum ${MIN_LEARNING_OBJECTIVE_LENGTH} characters)`;
            case len > MAX_LEARNING_OBJECTIVE_LENGTH:
              return `Learning objective is too long (maximum ${MAX_LEARNING_OBJECTIVE_LENGTH} characters)`;
            default:
              return "";
          }
        })(),
        standards: (() => {
          switch (true) {
            case !standards.length:
              return "You must include a standard";
            case standards.length > 3:
              return "You can only include up to 3 standards";
            default:
              return "";
          }
        })(),
      };

      setValidationErrors(errors);

      return !errors.learningObjective && !errors.standards;
    },
    [],
  );

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setValidationErrors({
      learningObjective: "",
      standards: "",
    });
  }, []);

  return {
    validationErrors,
    validateFields,
    reset,
    hasValidationErrors: Boolean(
      validationErrors.learningObjective || validationErrors.standards,
    ),
  };
};

export default useLessonPlannerValidation;
